import { useCallback, useEffect, useState } from "react"
import Modal from "react-responsive-modal"
import {
    getUserDeviceTokens,
    renameDeviceToken,
    removeDeviceToken,
} from "../services/userService"
import { formatRelativeTime } from "../utilities/helpers"

const PLATFORM_ICONS = {
    android: "bi-phone",
    ios: "bi-phone",
    web: "bi-laptop",
}

const deviceLabel = (device) =>
    device.device_name?.trim() || `${device.platform || "Mobile"} device`

/**
 * The signed-in user's registered push devices (the mobile apps), with rename
 * and remove. This browser is not listed: the web app has no push token.
 */
export default function DeviceList() {
    const [devices, setDevices] = useState([])
    const [status, setStatus] = useState("loading") // loading | ready | failed
    const [renameTarget, setRenameTarget] = useState(null)
    const [renameValue, setRenameValue] = useState("")
    const [removeTarget, setRemoveTarget] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [actionError, setActionError] = useState("")

    const loadDevices = useCallback(async () => {
        try {
            const response = await getUserDeviceTokens()
            setDevices(Array.isArray(response.data) ? response.data : [])
            setStatus("ready")
        } catch {
            setStatus("failed")
        }
    }, [])

    useEffect(() => {
        loadDevices()
    }, [loadDevices])

    const closeDialogs = () => {
        if (isSaving) return
        setRenameTarget(null)
        setRemoveTarget(null)
        setActionError("")
    }

    const openRename = (device) => {
        setRenameValue(device.device_name || "")
        setRenameTarget(device)
    }

    const confirmRename = async (event) => {
        event.preventDefault()
        if (!renameValue.trim()) return

        setIsSaving(true)
        setActionError("")
        try {
            await renameDeviceToken(renameTarget.id, renameValue.trim())
            setIsSaving(false)
            setRenameTarget(null)
            await loadDevices()
        } catch (error) {
            setIsSaving(false)
            setActionError(error.message || "Couldn't rename this device.")
        }
    }

    const confirmRemove = async () => {
        setIsSaving(true)
        setActionError("")
        try {
            await removeDeviceToken(removeTarget.id)
            setIsSaving(false)
            setRemoveTarget(null)
            await loadDevices()
        } catch (error) {
            setIsSaving(false)
            setActionError(error.message || "Couldn't remove this device.")
        }
    }

    if (status === "loading") {
        return (
            <div className="setting-empty" role="status">
                <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                ></span>
                Loading your devices...
            </div>
        )
    }

    if (status === "failed") {
        return (
            <div className="setting-empty">
                Couldn't load your devices.{" "}
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => {
                        setStatus("loading")
                        loadDevices()
                    }}
                >
                    Try again
                </button>
            </div>
        )
    }

    return (
        <>
            {devices.length === 0 ? (
                <div className="setting-empty">
                    No devices yet. Sign in to the Chaperon mobile app and it
                    will show up here.
                </div>
            ) : (
                devices.map((device) => (
                    <div className="setting-row" key={device.id}>
                        <span className="setting-icon" aria-hidden="true">
                            <i
                                className={`bi ${
                                    PLATFORM_ICONS[device.platform] ||
                                    "bi-hdd"
                                }`}
                            ></i>
                        </span>
                        <span className="setting-text">
                            <span className="setting-label">
                                {deviceLabel(device)}
                            </span>
                            <span className="setting-sub">
                                {device.last_seen_at
                                    ? `Last active ${formatRelativeTime(
                                          device.last_seen_at,
                                      ).toLowerCase()}`
                                    : "Not seen yet"}
                            </span>
                        </span>
                        <span className="setting-right">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary me-2"
                                onClick={() => openRename(device)}
                                aria-label={`Rename ${deviceLabel(device)}`}
                            >
                                <i className="bi bi-pencil"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setRemoveTarget(device)}
                                aria-label={`Remove ${deviceLabel(device)}`}
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </span>
                    </div>
                ))
            )}

            <Modal
                open={!!renameTarget}
                onClose={closeDialogs}
                center
                classNames={{ modal: "customModal" }}
            >
                <form onSubmit={confirmRename}>
                    <h4 className="mb-3">Rename device</h4>
                    <label htmlFor="device-name" className="form-label">
                        Device name
                    </label>
                    <input
                        id="device-name"
                        className="form-control"
                        value={renameValue}
                        onChange={(event) => setRenameValue(event.target.value)}
                        maxLength={100}
                        autoFocus
                    />
                    {actionError && (
                        <div className="text-danger small mt-2">
                            {actionError}
                        </div>
                    )}
                    <div className="d-flex gap-2 justify-content-end mt-4">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSaving || !renameValue.trim()}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closeDialogs}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={!!removeTarget}
                onClose={closeDialogs}
                center
                classNames={{ modal: "customModal" }}
            >
                <h4 className="mb-3">
                    <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                    Remove device
                </h4>
                <p>
                    Remove{" "}
                    <strong>
                        {removeTarget ? deviceLabel(removeTarget) : ""}
                    </strong>
                    ? It will stop receiving Chaperon notifications until you
                    sign in on it again.
                </p>
                {actionError && (
                    <div className="text-danger small mb-2">{actionError}</div>
                )}
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={confirmRemove}
                        disabled={isSaving}
                    >
                        {isSaving ? "Removing..." : "Remove device"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeDialogs}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                </div>
            </Modal>
        </>
    )
}
